import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowUp, ArrowDown, Eye, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { 
  ProcurementDiscrepancy, 
  DISCREPANCY_STATUS_LABELS 
} from '@/types/database/procurement-discrepancies';
import { formatCurrency } from '@/lib/utils';

interface DiscrepancyCardProps {
  discrepancy: ProcurementDiscrepancy;
  onResolve?: (id: string) => void;
  onMarkReviewed?: (id: string) => void;
  showActions?: boolean;
}

const statusVariantMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'destructive',
  reviewed: 'secondary',
  resolved: 'default',
  waived: 'outline',
};

const DiscrepancyCard: React.FC<DiscrepancyCardProps> = ({
  discrepancy,
  onResolve,
  onMarkReviewed,
  showActions = true,
}) => {
  const navigate = useNavigate();
  const isOverpayment = discrepancy.discrepancy_type === 'overpayment';
  const supplierName = discrepancy.procurement?.supplier?.name || 
                       discrepancy.procurement?.agent?.name || 
                       'Unknown Supplier';
  const productName = discrepancy.procurement?.product_name || 'Unknown Product';
  const shortId = discrepancy.procurement_id.substring(0, 8);

  return (
    <Card className={`border-l-4 ${isOverpayment ? 'border-l-orange-500' : 'border-l-blue-500'}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            {/* Header */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm text-muted-foreground">
                PROC-{shortId}
              </span>
              <Badge variant={statusVariantMap[discrepancy.status] || 'outline'}>
                {DISCREPANCY_STATUS_LABELS[discrepancy.status]}
              </Badge>
              <Badge 
                variant="outline" 
                className={isOverpayment ? 'border-orange-300 text-orange-700' : 'border-blue-300 text-blue-700'}
              >
                {isOverpayment ? (
                  <><ArrowUp className="h-3 w-3 mr-1" /> Overpaid</>
                ) : (
                  <><ArrowDown className="h-3 w-3 mr-1" /> Underpaid</>
                )}
              </Badge>
            </div>

            {/* Details */}
            <div className="space-y-1">
              <p className="font-medium">{productName}</p>
              <p className="text-sm text-muted-foreground">{supplierName}</p>
            </div>

            {/* Amounts */}
            <div className="grid grid-cols-3 gap-4 pt-2 text-sm">
              <div>
                <p className="text-muted-foreground">Originally Paid</p>
                <p className="font-medium">{formatCurrency(discrepancy.original_paid_amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Final Net Payable</p>
                <p className="font-medium">{formatCurrency(discrepancy.final_calculated_amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Difference</p>
                <p className={`font-bold ${isOverpayment ? 'text-orange-600' : 'text-blue-600'}`}>
                  {isOverpayment ? '+' : '-'}{formatCurrency(Math.abs(discrepancy.discrepancy_amount))}
                </p>
              </div>
            </div>

            {/* Date */}
            <p className="text-xs text-muted-foreground pt-1">
              Detected: {format(new Date(discrepancy.created_at), 'MMM d, yyyy h:mm a')}
              {discrepancy.resolved_at && (
                <> • Resolved: {format(new Date(discrepancy.resolved_at), 'MMM d, yyyy')}</>
              )}
            </p>
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate(`/finance/payments/discrepancies/${discrepancy.id}`)}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              {discrepancy.status === 'pending' && onMarkReviewed && (
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => onMarkReviewed(discrepancy.id)}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Review
                </Button>
              )}
              {(discrepancy.status === 'pending' || discrepancy.status === 'reviewed') && onResolve && (
                <Button 
                  size="sm"
                  onClick={() => onResolve(discrepancy.id)}
                >
                  Resolve
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DiscrepancyCard;
